import moment from 'moment';
import { canclePaymentLink } from '../../../services/apiService';
import { toast } from 'react-toastify';

// ─── Amount formatter ─────────────────────────────────────────────
const formatAmount = (amountInPaise) => {
  if (!amountInPaise && amountInPaise !== 0) return '—';
  const amount = amountInPaise / 100;
  return `₹${amount.toLocaleString('en-IN')}`;
};

// ─── Link cell renderer ───────────────────────────────────────────
function LinkCellRenderer(props) {
  const link = props?.data?.paymentLink;
  if (!link) return <span style={{ color: '#9CA3AF' }}>—</span>;
  return (
    <a
      href={link}
      target="_blank"
      rel="noreferrer"
      style={{
        color: '#4F46E5',
        fontFamily: 'monospace',
        fontSize: 12,
        textDecoration: 'none',
      }}
    >
      {link}
    </a>
  );
}

// ─── Date cell renderer ───────────────────────────────────────────
function DateCellRenderer(props) {
  const date = props?.data?.created_on;
  return (
    <span style={{ color: '#6B7280', fontSize: 12.5 }}>
      {date ? moment(date).format('DD MMM YYYY, hh:mm a') : '—'}
    </span>
  );
}

// ─── Name cell renderer (plain text, overridden by AvatarName in table) ──
function NameGetter(props) {
  const firstName = props?.data?.CreatedBy?.firstName || '';
  const lastName = props?.data?.CreatedBy?.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();
  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>
        {fullName || '—'}
      </span>
    </div>
  );
}

// ─── Status cell renderer (overridden by StatusPill in table) ─────
function StatusCellRenderer(props) {
  const status = props?.data?.paymentLinkStatus;
  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%', fontWeight: 500 }}>
      <span>{status || '—'}</span>
    </div>
  );
}

// ─── Amount cell renderer ─────────────────────────────────────────
function AmountCellRenderer(props) {
  const amount = props?.data?.actualAmount;
  return (
    <span style={{ fontWeight: 600, color: '#111827' }}>
      {formatAmount(amount)}
    </span>
  );
}

// ─── Generic text cell renderer ───────────────────────────────────
const textRenderer = (getValue) => {
  function TextCell(props) {
    const value = getValue(props);
    return (
      <span style={{ fontSize: 13, color: '#374151' }}>
        {value || '—'}
      </span>
    );
  }
  return TextCell;
};

// ─── Actions cell renderer ────────────────────────────────────────
function ActionsCellRenderer(props) {
  const { data } = props;
  if (data?.paymentLinkStatus !== 'created') {
    return <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>;
  }
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
      <button
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: '#DC2626',
          backgroundColor: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: 4,
          padding: '4px 12px',
          cursor: 'pointer',
        }}
        onClick={async () => {
          try {
            const apiData = { paymentLinkId: data?.id };
            const res = await canclePaymentLink(apiData);
            if (res?.successful) {
              // Refresh is handled by parent via gridApi
              if (props.context && props.context.onRefresh) {
                props.context.onRefresh();
              }
              toast.success('Payment Link Cancelled Successfully');
            }
          } catch (error) {
            toast.error('Failed to cancel payment link');
          }
        }}
      >
        Cancel
      </button>
    </div>
  );
}

// ─── Column Definitions ───────────────────────────────────────────
export const columnDefs = (onGridReady, gridApi) => [
  {
    headerName: 'Date & Time',
    field: 'generatedAt',
    sortable: true,
    filter: false,
    width: 180,
    cellRenderer: DateCellRenderer,
    valueGetter: (params) => params?.data?.created_on,
    comparator: (a, b) => (a || 0) - (b || 0),
  },
  {
    headerName: 'Generated By',
    field: 'name',
    sortable: false,
    filter: false,
    width: 180,
    cellRenderer: NameGetter,
    valueGetter: (params) => {
      const fn = params?.data?.CreatedBy?.firstName || '';
      const ln = params?.data?.CreatedBy?.lastName || '';
      return `${fn} ${ln}`.trim();
    },
  },
  {
    headerName: 'Status',
    field: 'linkStatus',
    sortable: false,
    filter: false,
    width: 130,
    cellRenderer: StatusCellRenderer,
    valueGetter: (params) => params?.data?.paymentLinkStatus,
  },
  {
    headerName: 'Amount',
    field: 'actualAmount',
    sortable: false,
    filter: false,
    width: 130,
    cellRenderer: AmountCellRenderer,
    valueGetter: (params) => params?.data?.actualAmount,
  },
  {
    headerName: 'Link Type',
    field: 'linkType',
    sortable: false,
    filter: false,
    width: 130,
    cellRenderer: textRenderer((p) => p?.data?.paymentLinkType),
    valueGetter: (params) => params?.data?.paymentLinkType,
  },
  {
    headerName: 'Payment Link',
    field: 'paymentLink',
    sortable: false,
    filter: false,
    width: 220,
    cellRenderer: LinkCellRenderer,
    valueGetter: (params) => params?.data?.paymentLink,
  },
  {
    headerName: 'Student Email',
    field: 'studentEmail',
    sortable: false,
    filter: false,
    width: 200,
    cellRenderer: textRenderer((p) => p?.data?.User?.email),
    valueGetter: (params) => params?.data?.User?.email,
  },
  {
    headerName: 'Course Type',
    field: 'courseType',
    sortable: false,
    filter: false,
    width: 150,
    cellRenderer: textRenderer((p) => p?.data?.courseType),
    valueGetter: (params) => params?.data?.courseType,
  },
  {
    headerName: 'Plan Type',
    field: 'planType',
    sortable: false,
    filter: false,
    width: 130,
    cellRenderer: textRenderer((p) => p?.data?.data?.planType),
    valueGetter: (params) => params?.data?.data?.planType,
  },
  {
    headerName: 'Enrollment',
    field: 'enrollmentStatus',
    sortable: false,
    filter: false,
    width: 140,
    cellRenderer: textRenderer((p) => p?.data?.enrollmentStatus),
    valueGetter: (params) => params?.data?.enrollmentStatus,
  },
  {
    headerName: 'Cohort',
    field: 'cohort',
    sortable: false,
    filter: false,
    width: 150,
    cellRenderer: textRenderer((p) => p?.data?.cohortName),
    valueGetter: (params) => params?.data?.cohortName,
  },
  {
    headerName: 'Actions',
    field: 'actions',
    sortable: false,
    filter: false,
    width: 120,
    pinned: 'right',
    cellRenderer: ActionsCellRenderer,
  },
];

// ─── Default Column Def ───────────────────────────────────────────
export const defaultColDef = {
  filter: false,
  sortable: true,
  resizable: true,
  suppressMenu: false,
  cellStyle: {
    paddingLeft: 20,
    paddingRight: 20,
    fontSize: 13,
    color: '#374151',
  },
  headerClass: 'ag-header-cell-custom',
};
